import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { FiEye, FiX } from 'react-icons/fi';
import { colors } from '../theme/modernTheme';
import WhatsAppTemplatePreview from './WhatsAppTemplatePreview';
import { API_BASE_URL } from '../services/api';

/**
 * Trust Master View Component
 * Displays trust master details in read-only mode
 *
 * @param {Object} props
 * @param {Object} props.trustMaster - Trust master object to display
 * @param {Object} props.whatsappTemplate - WhatsApp template for display
 * @param {Object} props.emailTemplate - Email template for display
 */
export default function TrustMasterView({
  trustMaster,
  whatsappTemplate = null,
  emailTemplate = null,
  templatesLoading = false,
}) {
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [whatsappPreviewOpen, setWhatsappPreviewOpen] = useState(false);

  if (!trustMaster) {
    return null;
  }

  // Helper function to render label-value pairs
  const renderField = (label, value, fullWidth = false) => (
    <Grid item xs={12} md={fullWidth ? 12 : 6}>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: colors.text.secondary,
            fontWeight: 500,
            textTransform: 'uppercase',
            fontSize: '0.7rem',
            letterSpacing: '0.5px',
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: colors.text.primary,
            fontSize: '0.875rem',
          }}
        >
          {value || '-'}
        </Typography>
      </Box>
    </Grid>
  );

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // Handle both absolute paths and relative paths
    // Absolute paths contain the full file system path, we need to extract the uploads/... part
    // Look for 'uploads/' in the path and take everything from there
    const uploadsIndex = imagePath.indexOf('uploads/');
    let relativePath;
    if (uploadsIndex !== -1) {
      relativePath = '/' + imagePath.substring(uploadsIndex);
    } else {
      // If it's already a relative path starting with uploads
      relativePath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    }
    // Prepend the backend base URL (remove /api/v1 suffix to get base URL)
    const backendBaseUrl = API_BASE_URL.replace('/api/v1', '');
    return `${backendBaseUrl}${relativePath}`;
  };

  // Helper function to render image
  const renderImage = (label, imagePath) => {
    const imageUrl = getImageUrl(imagePath);

    return (
      <Grid item xs={12} md={6}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              fontWeight: 500,
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              letterSpacing: '0.5px',
            }}
          >
            {label}
          </Typography>
          {imageUrl ? (
            <Card
              sx={{
                width: '100%',
                border: `1px solid ${colors.border.main}`,
                borderRadius: '4px',
              }}
            >
              <CardMedia
                component="img"
                image={imageUrl}
                alt={label}
                sx={{
                  height: 200,
                  objectFit: 'contain',
                  backgroundColor: colors.background.default,
                  p: 0.5,
                }}
                onError={(e) => {
                  console.error(`Failed to load image: ${imageUrl}`);
                  console.error(`Original path: ${imagePath}`);
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML =
                    '<div style="padding: 16px; text-align: center; color: #999;">Failed to load image</div>';
                }}
              />
            </Card>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: colors.text.secondary,
                fontStyle: 'italic',
              }}
            >
              No image uploaded
            </Typography>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <Box>
      {/* Basic Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.primary.main,
            mb: 2,
            pb: 1,
            borderBottom: `2px solid ${colors.primary.main}`,
          }}
        >
          Basic Information
        </Typography>
        <Grid container spacing={2}>
          {renderField('Trust Name', trustMaster.trustName)}
          {renderField('Receipt Prefix', trustMaster.receiptPrefix)}
        </Grid>
      </Box>

      {/* Images Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          {renderImage('Header Image', trustMaster.headerImagePath)}
          {renderImage('Footer Image', trustMaster.footerImagePath)}
        </Grid>
      </Box>

      {/* Footer Text Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          {renderField('Footer Text', trustMaster.footerText, true)}
        </Grid>
      </Box>

      {/* Communication Templates Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.primary.main,
            mb: 2,
            pb: 1,
            borderBottom: `2px solid ${colors.primary.main}`,
          }}
        >
          Communication Templates
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                  letterSpacing: '0.5px',
                }}
              >
                Email Template
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {templatesLoading ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: '0.875rem',
                    }}
                  >
                    loading...
                  </Typography>
                ) : emailTemplate ? (
                  <>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.text.primary,
                        fontSize: '0.875rem',
                      }}
                    >
                      {emailTemplate.name}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<FiEye />}
                      onClick={() => setEmailPreviewOpen(true)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        color: colors.primary.main,
                        backgroundColor: 'transparent !important',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: colors.primary.dark,
                        },
                      }}
                    >
                      View
                    </Button>
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.text.primary,
                      fontSize: '0.875rem',
                    }}
                  >
                    -
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  textTransform: 'uppercase',
                  fontWeight: 400,
                  color: colors.text.secondary,
                }}
              >
                WhatsApp Template
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {templatesLoading ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: '0.875rem',
                    }}
                  >
                    loading...
                  </Typography>
                ) : whatsappTemplate ? (
                  <>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.text.primary,
                        fontSize: '0.875rem',
                      }}
                    >
                      {whatsappTemplate.name}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<FiEye />}
                      onClick={() => setWhatsappPreviewOpen(true)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        color: colors.primary.main,
                        backgroundColor: 'transparent !important',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: colors.primary.dark,
                        },
                      }}
                    >
                      View
                    </Button>
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.text.primary,
                      fontSize: '0.875rem',
                    }}
                  >
                    -
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Notes Section */}
      {trustMaster.notes && (
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            {renderField('Notes', trustMaster.notes, true)}
          </Grid>
        </Box>
      )}

      {/* Email Template Preview Dialog */}
      <Dialog
        open={emailPreviewOpen}
        onClose={() => setEmailPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '4px 4px 0 0 !important',
            textTransform: 'capitalize',
            borderBottom: `1px solid ${colors.border.main}`,
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>Email Template Preview</Typography>
          <IconButton
            onClick={() => setEmailPreviewOpen(false)}
            sx={{
              color: colors.text.secondary,
              '&:hover': {
                color: colors.text.primary,
              },
            }}
          >
            <FiX />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 2,
            mt: 1,
            borderRadius: '4px !important',
          }}
        >
          {emailTemplate && (
            <Card
              sx={{
                border: `1px solid ${colors.border.main}`,
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <iframe
                title="Email Template Preview"
                srcDoc={emailTemplate.html_body}
                style={{
                  width: '100%',
                  minHeight: '400px',
                  maxHeight: '600px',
                  border: 'none',
                  display: 'block',
                }}
                onLoad={(e) => {
                  const iframe = e.target;
                  try {
                    const height = iframe.contentWindow.document.body.scrollHeight;
                    iframe.style.height = Math.min(Math.max(height, 400), 600) + 'px';

                    // Disable clicks on links and buttons (preserve scrolling)
                    const iframeDoc = iframe.contentWindow.document;
                    const style = iframeDoc.createElement('style');
                    style.textContent =
                      'a, button, input, select, textarea { pointer-events: none !important; cursor: default !important; }';
                    iframeDoc.head.appendChild(style);

                    // Prevent all link clicks
                    iframeDoc.addEventListener(
                      'click',
                      (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        return false;
                      },
                      true,
                    );
                  } catch (err) {
                    iframe.style.height = '500px';
                  }
                }}
              />
            </Card>
          )}
        </DialogContent>
      </Dialog>

      {/* WhatsApp Template Preview Dialog */}
      <Dialog
        open={whatsappPreviewOpen}
        onClose={() => setWhatsappPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '4px !important',
          }}
        >
          <Typography sx={{ fontWeight: 500, ml: 3 }}>Whatsapp Template Preview</Typography>
          <IconButton onClick={() => setWhatsappPreviewOpen(false)}>
            <FiX />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {whatsappTemplate && <WhatsAppTemplatePreview template={whatsappTemplate} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
